import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppBar from '../components/AppBar';
import { Chip, EmptyState, InfiniteSentinel, SearchBar, Skeletons, dt } from '../components/kit';
import { usePaginatedList } from '../hooks/usePaginatedList';
import * as lettersApi from '../api/coAgencyLetters';
import { letterCoaShareLabel, letterCidShareLabel } from '../lib/coAgencyShare';

// Filters send the raw DB enum; the labels say what the state means to an AGENT.
//
// PENDING is a private draft, not something waiting on HQ — nobody verifies letters
// any more — and VERIFIED means the agent issued it. Both values stay in the database
// because renaming them would have meant migrating every row to change two chips.
// REJECTED is omitted: nothing writes it, so the filter would always come back empty.
const STATUS_FILTERS = [
  { key: '', label: 'All' },
  { key: 'PENDING', label: 'Draft' },
  { key: 'VERIFIED', label: 'Issued' },
  { key: 'VOIDED', label: 'Voided' },
];

// FULL-SCREEN · /letters — the agent's own co-agency/referral letters inbox.
export default function LettersPage() {
  const nav = useNavigate();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const params = useMemo(() => ({ status: status || undefined, search: search || undefined }), [status, search]);
  // paginated list — sends { page, limit, status, search } and appends pages on scroll
  const list = usePaginatedList(lettersApi.listCoAgencyLetters, { limit: 10, params });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <AppBar back title="Co-agency letters" sub="issue one when the terms are settled" />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <SearchBar onSearch={setSearch} placeholder="Search ref, co-agency or address…" />
        <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
          {STATUS_FILTERS.map((f) => {
            const active = f.key === status;
            return (
              <div key={f.key} onClick={() => setStatus(f.key)} style={{ flex: 'none', fontSize: 12.5, fontWeight: 600, padding: '6px 13px', borderRadius: 999, cursor: 'pointer', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}>
                {f.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.loading ? (
          <Skeletons n={3} />
        ) : list.empty ? (
          <EmptyState title="No letters yet" sub={search || status ? 'Nothing matches your search or filter.' : 'Draft a co-agency letter, then issue it once the terms are settled.'} />
        ) : (
          <>
            {list.items.map((l) => {
              // Property + date line (mock's second metadata row). The list endpoint does
              // not yet return property_address / a date field (see sharedChangesNeeded);
              // build defensively so the line is simply omitted until those fields arrive,
              // rather than rendering blank " · " separators.
              const dateRaw = l.letter_date || l.created_at;
              const meta = [l.property_address, dateRaw ? dt(dateRaw) : null].filter(Boolean);
              return (
                <div key={l.id} onClick={() => nav(`/letters/${l.id}`)} style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '15px 16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'ui-monospace,monospace' }}>{l.letter_ref}</span>
                    <Chip label={l.status} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{l.co_agency} · split {letterCidShareLabel(l)} / {letterCoaShareLabel(l)}</div>
                  {meta.length > 0 && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{meta.join(' · ')}</div>}
                </div>
              );
            })}
            <InfiniteSentinel hasMore={list.hasMore} loading={list.loadingMore} onReach={list.loadMore} />
          </>
        )}
        <div style={{ height: 66 }} />
      </div>

      <div onClick={() => nav('/letters/new')} style={{ position: 'absolute', right: 18, bottom: 18, width: 54, height: 54, borderRadius: 50, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, cursor: 'pointer', boxShadow: '0 8px 20px color-mix(in srgb,var(--primary) 45%,transparent)', zIndex: 5 }}>＋</div>
    </div>
  );
}
